import React, { useCallback, useEffect, useState } from 'react';
import useAuthGuard from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../config';
import apiFetch from '../utils/apiFetch';

/**
 * ユーザーからの「この動画はこの店のものではない」（SKILLCRANE-251）。
 *
 * 紐付けは自動マッチャが付けている。タイトルと説明文から店を当てているので
 * **誤りは必ず出る** —— ハッシュタグの羅列・リンク先動画のタイトル・
 * 景品名と地名の衝突で、実際に5回デプロイをかけて潰している。
 * それでも残った誤りに気づけるのは、その店を知っている人だけ。
 *
 * ## 報告だけでは何も外れない
 *
 * 押すだけで動画が消せる状態にはしない。ここで人が決める。
 *
 * ## どちらを押しても「人が決めた」印が立つ
 *
 * マッチャは store_id が NULL の動画を拾うので、外しただけでは**次の実行で
 * 同じ店に付き直す**。「問題なし」で残した紐付けも、あとから機械に
 * 付け替えられては判断した意味が無い。どちらの判断でも印を立てる。
 */

const REASON_LABEL = {
  wrong_store: 'この店の動画ではない',
  not_crane: 'クレーンゲームの動画ではない',
  unavailable: '再生できない',
  inappropriate: '内容が不適切',
  other: 'その他',
};

export default function VideoLinkReports() {
  useAuthGuard();

  const [items, setItems] = useState(null);
  const [pending, setPending] = useState(0);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState('');

  const load = useCallback(async (signal) => {
    setError('');
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/video-link-reports?limit=50`, { signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      setItems(Array.isArray(body.data?.reports) ? body.data.reports : []);
      setPending(body.data?.pending || 0);
    } catch (e) {
      if (e.name !== 'AbortError') setError(e.message || 'Network Error');
    }
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    load(ac.signal);
    return () => ac.abort();
  }, [load]);

  // **判定したら取り直す。** 1件の判断で片付く範囲がサーバ側で決まっている
  // （外したときはその紐付けの報告を全部、問題なしのときは同じ理由だけ）ので、
  // 手元で行を消すと画面と実態がずれる
  const resolve = async (report, unlink) => {
    setBusyId(report.id);
    setError('');
    try {
      const res = await apiFetch(
        `${API_BASE_URL}/admin/video-link-reports/${report.id}/resolve`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ unlink }),
        },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error?.message || `HTTP ${res.status}`);
      }
      // **取り直す。** 1件押すと同じ紐付けの報告がまとめて片付くことがあり
      // （外したときは理由を問わず全部）、手元で引き算すると件数がずれる。
      // 51件目以降も、消した行のぶんだけ繰り上がって出てくる
      await load();
    } catch (e) {
      setError(e.message || 'Network Error');
    } finally {
      setBusyId('');
    }
  };

  return (
    <Layout>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h4 className="mb-1">動画の紐付けへの報告</h4>
          <p className="text-muted mb-0 small">
            読んだ人から「この動画はこの店のものではない」と届いたものです。
            <strong>報告だけでは何も外れません。</strong>ここで決めると、
            以降その動画は自動マッチャの対象から外れます。
          </p>
        </div>
        <div className="text-end small text-muted">
          未判定 <strong>{pending}</strong>
        </div>
      </div>

      {error && <div className="alert alert-danger py-2 small">{error}</div>}

      {items === null && <p className="text-muted small">読み込み中…</p>}

      {items !== null && items.length === 0 && (
        <div className="card">
          <div className="card-body text-center text-muted small">
            未判定の報告はありません。
          </div>
        </div>
      )}

      {items !== null && items.length > 0 && (
        <div className="list-group">
          {items.map((r) => (
            <div key={r.id} className="list-group-item">
              <div className="d-flex flex-wrap align-items-start justify-content-between gap-2">
                <div className="min-w-0">
                  <div className="d-flex align-items-center gap-2 mb-1">
                    {/* 深刻なものを先に見せる。誤った紐付けと不適切な内容は、
                        出ている間ずっと嘘をついている */}
                    <span
                      className={`badge ${
                        r.serious ? 'bg-danger' : 'bg-secondary-subtle text-secondary-emphasis'
                      }`}
                    >
                      {REASON_LABEL[r.reason] || r.reason}
                    </span>
                    {r.reporters > 1 && (
                      <span className="badge bg-warning-subtle text-warning-emphasis">
                        {r.reporters}人が報告
                      </span>
                    )}
                    {!r.stillLinked && (
                      <span className="badge bg-light text-muted">すでに外れている</span>
                    )}
                  </div>

                  <div className="fw-semibold">{r.videoTitle || '(題名なし)'}</div>
                  <div className="small text-muted">
                    {r.channelTitle && <>チャンネル: {r.channelTitle}　</>}
                    紐付け先: <strong>{r.storeName || '(店舗不明)'}</strong>
                  </div>
                  {r.note && <div className="small mt-1">「{r.note}」</div>}
                  <a
                    href={`https://www.youtube.com/watch?v=${r.videoId}`}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="small"
                  >
                    動画を見る（YouTube）
                  </a>
                </div>

                <div className="d-flex flex-column gap-1">
                  <button
                    type="button"
                    className="btn btn-sm btn-danger"
                    disabled={busyId === r.id}
                    onClick={() => resolve(r, true)}
                  >
                    紐付けを外す
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    disabled={busyId === r.id}
                    onClick={() => resolve(r, false)}
                  >
                    問題なし
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Layout>
  );
}
